/**
 * Typed builder for help-center URLs.
 *
 * Every in-app help link goes through {@link helpUrl} so we can swap the help
 * destination via a single env var and so the compiler can prove that the slug
 * exists in the canonical TOC manifest. {@link HelpSlug} is derived directly
 * from `HELP_ARTICLES` in the branding-site package, so a slug that ships in
 * the app must also exist on the marketing site — an unknown slug is a compile
 * error, not a runtime 404.
 *
 * @see uis/branding-site/src/help/help-toc.ts for the source of truth.
 */

// Imported via the `@branding-site/help-toc` path alias declared in
// `tsconfig.json` + `vite.config.ts` + `vitest.config.ts`. This sidesteps the
// need to publish the branding-site package as a workspace dep while keeping
// the union in lockstep with the canonical manifest.
import { HELP_ARTICLES } from '@branding-site/help-toc';

/**
 * Drops the leading `help/` from a TOC slug so the helper accepts the form an
 * in-app caller would naturally pass (`'policies/create'`, not
 * `'help/policies/create'`). The root TOC slug `'help'` becomes the empty
 * string — `helpUrl('')` resolves to `.../help`.
 */
type StripHelpPrefix<S> = S extends `help/${infer R}` ? R : S extends 'help' ? '' : never;

/**
 * Union of every valid in-app help slug. Derived from the canonical
 * `HELP_ARTICLES` manifest, so adding or renaming an article in the branding
 * site automatically flows through to the type used here.
 */
export type HelpSlug = StripHelpPrefix<(typeof HELP_ARTICLES)[number]['slug']>;

/**
 * Production fallback used when no `VITE_HELP_BASE` is set and the runtime
 * hostname doesn't match a known pattern (e.g. SSR, localhost, preview hosts).
 */
const PROD_HELP_BASE = 'https://www.yesaccedo.com';

/**
 * Resolve the help-center base URL for the current runtime.
 *
 * Resolution order (first match wins):
 *
 *   1. `VITE_HELP_BASE` env var — explicit override, useful for local preview,
 *      tests, and one-off staging deploys.
 *   2. Runtime `window.location.hostname` pattern match:
 *      - `*.yesaccedo.com` (or bare `yesaccedo.com`) → the prod help site.
 *      - `*.dev.wadsworth.dev` → the per-TM dev branding deploy. Strips the
 *        tenant + sub-account segments and keeps the last 4 segments, so
 *        `acme.546385742337.436484810012.dev.wadsworth.dev` maps to
 *        `https://436484810012.dev.wadsworth.dev`.
 *   3. Final fallback — the production help site.
 *
 * We derive at call time rather than at module-eval so SSR (no `window`) and
 * tests that swap `window.location` between cases both behave correctly. The
 * cost is negligible — one string check per help link click — and we sidestep
 * the gitignored-`.env.*` problem that hardcoded the dev deploy to prod help.
 */
function deriveHelpBase(): string {
    const override = import.meta.env.VITE_HELP_BASE;
    if (typeof override === 'string' && override.length > 0) {
        return override;
    }

    if (typeof window === 'undefined') {
        return PROD_HELP_BASE;
    }

    const { hostname, protocol } = window.location;

    if (hostname === 'yesaccedo.com' || hostname.endsWith('.yesaccedo.com')) {
        return `${protocol}//www.yesaccedo.com`;
    }

    if (hostname.endsWith('.dev.wadsworth.dev')) {
        const parts = hostname.split('.');
        // Need at least 4 segments to safely strip the tenant prefix.
        // Examples that satisfy this: `dev.wadsworth.dev` is only 3 segments and
        // would NOT match (rightly — we don't know which dev TM to point at);
        // `<tm>.dev.wadsworth.dev` is 4 segments and maps to itself;
        // `<tenant>.<sub>.<tm>.dev.wadsworth.dev` is 6 and maps to the last 4.
        if (parts.length >= 4) {
            return `${protocol}//${parts.slice(-4).join('.')}`;
        }
    }

    return PROD_HELP_BASE;
}

/**
 * Build a help-center URL for the given slug.
 *
 * Passing the empty string `''` resolves to the root TOC (`/help`). Any other
 * slug must appear in {@link HELP_ARTICLES} — the compiler enforces this so
 * stale or mistyped slugs are caught at build time, not when a user clicks a
 * dead link.
 *
 * @param slug Article slug (without the leading `help/`), or `''` for the TOC.
 * @returns Absolute URL on the configured help-center base.
 */
export function helpUrl(slug: HelpSlug): string {
    const base = deriveHelpBase();
    return slug === '' ? `${base}/help` : `${base}/help/${slug}`;
}
